import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  clearSelectedTextTestimonial,
  clearTextTestimonialStatus,
  getOneTextTestimonial,
  updateTextTestimonial,
} from 'slice/textTestimonialSlice';

const ensureTrailingSlash = (value = '') => (value.endsWith('/') ? value : `${value}/`);

const isAbsoluteUrl = (value = '') => /^https?:\/\//i.test(value);

const isManagedUploadPath = (value = '') =>
  typeof value === 'string' && value.replace(/\\/g, '/').startsWith('text_testimonials/');

const isActiveValue = (value) => {
  if (value === 0 || value === false) {
    return false;
  }

  const normalized = String(value ?? '').trim().toLowerCase();
  return normalized !== '0' && normalized !== 'false' && normalized !== 'inactive' && normalized !== 'no';
};

const getProfileImageUrl = (value = '') => {
  if (!value) {
    return '';
  }

  if (isAbsoluteUrl(value)) {
    return value;
  }

  const normalized = String(value).replace(/\\/g, '/').replace(/^\/+/, '');
  const uploadsBase = process.env.REACT_APP_BACKEND_URL_REL
    ? ensureTrailingSlash(process.env.REACT_APP_BACKEND_URL_REL)
    : '';

  if (uploadsBase && isManagedUploadPath(normalized)) {
    return `${uploadsBase}${normalized}`;
  }

  const backendBase = process.env.REACT_APP_BACKEND ? ensureTrailingSlash(process.env.REACT_APP_BACKEND) : '';
  return backendBase ? `${backendBase}${normalized}` : normalized;
};

const getErrorMessage = (error) =>
  error?.response?.data?.message || error?.data?.message || error?.message || 'Unable to load testimonial details.';

const Update = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, selectedItem, lastAction } = useSelector((state) => state.textTestimonial);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [formData, setFormData] = useState({
    full_name: '',
    review_text: '',
    rating: 5,
    review_time: '1 year ago',
    is_active: 1,
    profile_image_url: '',
    profile_image_file: null,
  });

  useEffect(() => {
    dispatch(clearTextTestimonialStatus());
    dispatch(getOneTextTestimonial(id));
    setHasSubmitted(false);

    return () => {
      dispatch(clearSelectedTextTestimonial());
      dispatch(clearTextTestimonialStatus());
    };
  }, [dispatch, id]);

  useEffect(() => {
    if (!selectedItem || String(selectedItem?.id || '') !== String(id)) {
      return;
    }

    setFormData({
      full_name: selectedItem.full_name || '',
      review_text: selectedItem.review_text || '',
      rating: Number(selectedItem.rating) || 5,
      review_time: selectedItem.review_time || '1 year ago',
      is_active: isActiveValue(selectedItem.is_active) ? 1 : 0,
      profile_image_url: isManagedUploadPath(selectedItem.profile_image) ? '' : selectedItem.profile_image || '',
      profile_image_file: null,
    });
    setRemoveExistingImage(false);
  }, [id, selectedItem]);

  useEffect(() => {
    if (!formData.profile_image_file) {
      setProfileImagePreview('');
      return undefined;
    }

    const objectUrl = URL.createObjectURL(formData.profile_image_file);
    setProfileImagePreview(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [formData.profile_image_file]);

  useEffect(() => {
    if (hasSubmitted && lastAction === 'update' && !loading && !error) {
      toast('Text testimonial updated successfully!');
      dispatch(clearTextTestimonialStatus());
      setHasSubmitted(false);
      navigate('/text-testimonial');
    }
  }, [dispatch, error, hasSubmitted, lastAction, loading, navigate]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: name === 'rating' || name === 'is_active' ? Number(value) : value,
    }));

    if (name === 'profile_image_url') {
      setRemoveExistingImage(false);
    }
  };

  const handleProfileImageChange = (event) => {
    const file = event.target.files?.[0] || null;

    setFormData((prevState) => ({
      ...prevState,
      profile_image_file: file,
    }));
    setRemoveExistingImage(false);
  };

  const handleRemoveImage = () => {
    setRemoveExistingImage(true);
    setProfileImagePreview('');
    setFormData((prevState) => ({
      ...prevState,
      profile_image_file: null,
      profile_image_url: '',
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!formData.full_name.trim()) {
      toast('Full name is required.');
      return;
    }

    if (!formData.review_text.trim()) {
      toast('Review text is required.');
      return;
    }

    const payload = new FormData();
    payload.append('full_name', formData.full_name.trim());
    payload.append('review_text', formData.review_text.trim());
    payload.append('rating', String(formData.rating));
    payload.append('review_time', formData.review_time.trim() || '1 year ago');
    payload.append('is_active', String(formData.is_active));

    if (formData.profile_image_file) {
      payload.append('profile_image', formData.profile_image_file);
    } else if (formData.profile_image_url.trim()) {
      payload.append('profile_image', formData.profile_image_url.trim());
    } else if (removeExistingImage) {
      payload.append('profile_image', '');
    }

    setHasSubmitted(true);
    dispatch(updateTextTestimonial({ id, data: payload }));
  };

  const isCurrentItemLoaded = String(selectedItem?.id || '') === String(id);
  const currentProfileImageUrl =
    profileImagePreview ||
    getProfileImageUrl(formData.profile_image_url.trim()) ||
    (!removeExistingImage && isManagedUploadPath(selectedItem?.profile_image) ? getProfileImageUrl(selectedItem.profile_image) : '');

  return (
    <>
      <div className="container card my-5">
        {!isCurrentItemLoaded && loading && <p className="mx-3 mt-4">Loading testimonial details...</p>}

        {!isCurrentItemLoaded && !loading && error && <p className="mx-3 mt-4 text-danger">{getErrorMessage(error)}</p>}

        {isCurrentItemLoaded && (
          <form onSubmit={handleSubmit}>
            <div className="row p-3">
            <div className="col-sm-6 mt-3">
              <label htmlFor="updateTestimonialFullName">Full Name</label>
              <input
                id="updateTestimonialFullName"
                name="full_name"
                type="text"
                className="form-control"
                value={formData.full_name}
                onChange={handleChange}
              />
            </div>

            <div className="col-sm-3 mt-3">
              <label htmlFor="updateTestimonialRating">Rating</label>
              <select
                id="updateTestimonialRating"
                name="rating"
                className="form-control"
                value={formData.rating}
                onChange={handleChange}
              >
                {[5, 4, 3, 2, 1].map((rating) => (
                  <option key={rating} value={rating}>
                    {rating} / 5
                  </option>
                ))}
              </select>
            </div>

            <div className="col-sm-3 mt-3">
              <label htmlFor="updateTestimonialStatus">Status</label>
              <select
                id="updateTestimonialStatus"
                name="is_active"
                className="form-control"
                value={formData.is_active}
                onChange={handleChange}
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </select>
            </div>

            <div className="col-sm-12 mt-4">
              <label htmlFor="updateTestimonialReviewText">Review Text</label>
              <textarea
                id="updateTestimonialReviewText"
                name="review_text"
                rows={5}
                className="form-control"
                value={formData.review_text}
                onChange={handleChange}
              />
            </div>

            <div className="col-sm-6 mt-4">
              <label htmlFor="updateTestimonialReviewTime">Review Time</label>
              <input
                id="updateTestimonialReviewTime"
                name="review_time"
                type="text"
                className="form-control"
                value={formData.review_time}
                onChange={handleChange}
              />
            </div>

            <div className="col-sm-6 mt-4">
              <label htmlFor="updateTestimonialProfileImageFile">Replace Profile Image</label>
              <input
                id="updateTestimonialProfileImageFile"
                type="file"
                name="profile_image_file"
                className="form-control btn-block filebg"
                accept="image/*"
                onChange={handleProfileImageChange}
              />
            </div>

            <div className="col-sm-12 mt-4">
              <label htmlFor="updateTestimonialProfileImageUrl">Profile Image URL</label>
              <input
                id="updateTestimonialProfileImageUrl"
                name="profile_image_url"
                type="url"
                className="form-control"
                value={formData.profile_image_url}
                onChange={handleChange}
              />
            </div>

            {currentProfileImageUrl && (
              <div className="col-sm-4 mt-4">
                <div className="adminlables">Current Image</div>
                <div>
                  <img
                    src={currentProfileImageUrl}
                    alt={formData.full_name || 'testimonial'}
                    className="img-thumbnail"
                    style={{ maxWidth: 220, width: '100%', objectFit: 'cover' }}
                  />
                </div>
              </div>
            )}

            {selectedItem?.profile_image && !removeExistingImage && (
              <div className="col-sm-12 mt-3">
                <button type="button" className="btn editbtn" onClick={handleRemoveImage}>
                  Remove Current Image
                </button>
              </div>
            )}
          </div>

          <div className="mt-3 text-right">
            <button type="submit" className="btn btn-success" disabled={loading}>
              Update Text Testimonial
            </button>
          </div>
          </form>
        )}
      </div>
    </>
  );
};

export default Update;
